import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, UserRound } from 'lucide-react';
import type { AdminStaffProfile } from '@oracord/shared';
import { authClient } from '../lib/auth-client';
import { useMe } from '../hooks/useMe';
import { useAuthCapabilities } from '../hooks/useAuthCapabilities';
import { useSessionStore } from '../stores/session';
import { fetchStaffProfiles } from '../lib/staff-api';
import {
  staffProfileOptionLabel,
  staffLabel,
  staffProfilesReadyForSetupInvite,
  staffSetupInviteHint,
  staffWorkEmailLabel,
  activeStaffProfiles,
  pendingInviteEmails,
  normalizedStaffEmailSet,
  staffSetupStatusSummary,
  staffGooglePermissionsSummary,
  staffInviteActivationStatus,
} from '../lib/staff';
import { PageHeader, SectionCard, StatusBadge } from '../components/WebPresentation';
import { buildNumbersDrillthroughUrl } from '../lib/assignment-drillthrough';

// Members & invitations for the ACTIVE organization.
//
// Server-side truth reminders:
//   - Better Auth's own endpoints enforce org membership + role on every
//     call (inviteMember, removeMember, etc.). This UI gates buttons for
//     UX polish only — a user who bypasses the gate still hits the same
//     server check.
//   - We pass `organizationId` explicitly on every call (we don't use
//     Better Auth's "active organization" session field; our zustand
//     store is the client-side source of truth for the active org).

type Role = 'owner' | 'admin' | 'member';
type TeamAccessMode = 'mobile_app_only' | 'app_and_dashboard' | 'owner_dashboard';
type InviteMemberPayload = Parameters<typeof authClient.organization.inviteMember>[0] & { resend?: boolean };

function accessModeForRole(role: string): TeamAccessMode {
  if (role === 'owner') return 'owner_dashboard';
  if (role === 'admin') return 'app_and_dashboard';
  return 'mobile_app_only';
}

function roleForAccessMode(mode: TeamAccessMode): Role {
  if (mode === 'owner_dashboard') return 'owner';
  if (mode === 'app_and_dashboard') return 'admin';
  return 'member';
}

interface MemberRow {
  id: string;
  role: string;
  userId: string;
  user: { email: string; name: string };
}

interface InvitationRow {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string | Date;
}

export default function Members() {
  const { me } = useMe();
  const caps = useAuthCapabilities();
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);
  const active = me?.organizations.find(o => o.id === activeOrgId);
  const myRole = (active?.role ?? 'member') as Role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [staffProfiles, setStaffProfiles] = useState<AdminStaffProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const [mRes, iRes, profiles] = await Promise.all([
        authClient.organization.listMembers({ query: { organizationId: activeOrgId } }),
        authClient.organization.listInvitations({ query: { organizationId: activeOrgId } }),
        canManage ? fetchStaffProfiles() : Promise.resolve([]),
      ]);
      if (mRes.error) throw new Error(mRes.error.message || 'Failed to load members');
      if (iRes.error) throw new Error(iRes.error.message || 'Failed to load invitations');

      // listMembers returns `{ members, total }` on success.
      const membersData = (mRes.data as { members?: MemberRow[] } | null)?.members ?? [];
      setMembers(membersData);

      // listInvitations returns an array of InvitationRow; Phase 1 just
      // surfaces pending ones (accepted/canceled/rejected are noise in
      // the UI).
      const invitesData = Array.isArray(iRes.data) ? (iRes.data as InvitationRow[]) : [];
      setInvitations(invitesData.filter(i => i.status === 'pending'));
      setStaffProfiles(profiles);
    } catch (err) {
      setError((err as Error).message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingInviteEmailSet = useMemo(() => pendingInviteEmails(invitations), [invitations]);
  const memberEmailSet = useMemo(() => normalizedStaffEmailSet(members.map(member => ({ email: member.user.email }))), [members]);
  const invitationEmailEnabled = caps?.invitationEmailEnabled ?? false;
  const staffSetupSummaries = useMemo(
    () => activeStaffProfiles(staffProfiles).map(profile => ({
      profile,
      summary: staffSetupStatusSummary(profile, { pendingInviteEmails: pendingInviteEmailSet }),
      inviteActivation: staffInviteActivationStatus(profile, {
        pendingInviteEmails: pendingInviteEmailSet,
        memberEmails: memberEmailSet,
        invitationEmailEnabled,
      }),
    })),
    [invitationEmailEnabled, memberEmailSet, pendingInviteEmailSet, staffProfiles],
  );

  if (!activeOrgId) {
    return (
      <SectionCard className="p-5">
        <div className="text-sm font-medium text-slate-900">No organization selected</div>
        <p className="mt-1 text-xs text-slate-500">Choose an organization before reviewing access members.</p>
      </SectionCard>
    );
  }

  return (
    <div className="max-w-6xl space-y-5">
      <PageHeader
        eyebrow="Team"
        title="Team"
        description={(
          <>
            People who can use <strong>{active?.name}</strong>. Start with the owner, then add teammates with their company work emails.
          </>
        )}
        action={(
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm shadow-slate-200/70 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      />

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {canManage && (
        <StaffSetupControlCenter
          rows={staffSetupSummaries}
          members={members}
          loading={loading}
        />
      )}

      {canManage && (
        <InviteForm
          orgId={activeOrgId}
          invitationEmailEnabled={invitationEmailEnabled}
          staffProfiles={staffProfiles}
          onDone={load}
        />
      )}
    </div>
  );
}

// ─── Shared presentation helpers ──────────────────────────

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="px-5 py-10 text-center">
      <UserRound className="mx-auto h-8 w-8 text-slate-300" />
      <p className="mt-3 text-sm font-medium text-slate-900">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-xs leading-5 text-slate-500">{description}</p>
    </div>
  );
}


function StaffSetupControlCenter({
  rows,
  members,
  loading,
}: {
  rows: Array<{
    profile: AdminStaffProfile;
    summary: ReturnType<typeof staffSetupStatusSummary>;
    inviteActivation: ReturnType<typeof staffInviteActivationStatus>;
  }>;
  members: MemberRow[];
  loading: boolean;
}) {
  const ownerEmails = new Set(members.filter(member => member.role === 'owner').map(member => member.user.email.trim().toLowerCase()));
  const ownerUserIds = new Set(members.filter(member => member.role === 'owner').map(member => member.userId));
  const ownerRows = rows.filter(({ profile }) => ownerUserIds.has(profile.userId ?? '') || ownerEmails.has((profile.workEmail ?? '').trim().toLowerCase()));
  const teamRows = rows.filter(row => !ownerRows.some(owner => owner.profile.id === row.profile.id));

  return (
    <SectionCard className="overflow-hidden p-0">
      <header className="border-b border-slate-100 px-5 py-4">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Team</div>
        <h2 className="mt-1 text-lg font-semibold text-slate-950">Team members</h2>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
          Keep this list simple: owner first, then the people who need Oracord on their work account.
        </p>
      </header>

      {loading && rows.length === 0 ? (
        <div className="px-5 py-6 text-sm text-slate-500">Loading team members…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No Team profiles yet"
          description="Add the owner or first teammate with their company work email."
        />
      ) : (
        <div className="space-y-5 bg-white px-5 py-5">
          <TeamGroup title="Owner" empty="No owner Team profile yet.">
            {ownerRows.map(row => <TeamProfileCard key={row.profile.id} row={row} featured />)}
          </TeamGroup>
          <TeamGroup title="Team members" empty="No other teammates added yet.">
            {teamRows.map(row => <TeamProfileCard key={row.profile.id} row={row} />)}
          </TeamGroup>
        </div>
      )}
    </SectionCard>
  );
}

function TeamGroup({ title, empty, children }: { title: string; empty: string; children: ReactNode }) {
  const hasChildren = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{title}</h3>
      <div className="mt-2 space-y-3">
        {hasChildren ? children : <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-4 text-sm text-slate-500">{empty}</div>}
      </div>
    </section>
  );
}

function TeamProfileCard({
  row,
  featured = false,
}: {
  row: {
    profile: AdminStaffProfile;
    summary: ReturnType<typeof staffSetupStatusSummary>;
    inviteActivation: ReturnType<typeof staffInviteActivationStatus>;
  };
  featured?: boolean;
}) {
  const { profile, inviteActivation } = row;
  const googlePermissions = staffGooglePermissionsSummary(profile);
  const hasBusinessLine = (profile.usage?.numbers ?? 0) > 0;
  return (
    <div className={`rounded-2xl border p-4 ${featured ? 'border-blue-100 bg-blue-50/50' : 'border-slate-100 bg-slate-50/70'}`}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="truncate text-base font-semibold text-slate-950">{staffLabel({ profile })}</div>
          <div className="mt-1 text-sm text-slate-500">{profile.workEmail || 'No work email saved'}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <SimpleStatus done={inviteActivation.done} label={inviteActivation.done ? 'Invite sent' : 'Invite needed'} />
          <SimpleStatus done={googlePermissions.state === 'connected'} label={googlePermissions.state === 'connected' ? 'Workspace connected' : 'Connect Workspace'} />
          <SimpleStatus
            done={hasBusinessLine}
            label={hasBusinessLine ? 'Business line connected' : 'Set up number'}
            href={hasBusinessLine ? undefined : buildNumbersDrillthroughUrl({ staffProfileId: profile.id })}
          />
        </div>
      </div>
    </div>
  );
}

function SimpleStatus({ done, label, href }: { done: boolean; label: string; href?: string }) {
  const badge = <StatusBadge tone={done ? 'emerald' : 'slate'} size="sm">{label}</StatusBadge>;
  if (!href) return badge;
  return (
    <Link to={href} className="rounded-full transition hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2">
      {badge}
    </Link>
  );
}

// ─── Invite ───────────────────────────────────────────────

function InviteForm({
  orgId,
  invitationEmailEnabled,
  staffProfiles,
  onDone,
}: {
  orgId: string;
  invitationEmailEnabled: boolean;
  staffProfiles: AdminStaffProfile[];
  onDone: () => void | Promise<void>;
}) {
  const inviteReadyProfiles = useMemo(() => staffProfilesReadyForSetupInvite(staffProfiles), [staffProfiles]);
  const [selectedStaffProfileId, setSelectedStaffProfileId] = useState('');
  const selectedStaffProfile = inviteReadyProfiles.find(profile => profile.id === selectedStaffProfileId) ?? null;
  const selectedStaffEmail = staffWorkEmailLabel(selectedStaffProfile);
  const inviteHint = staffSetupInviteHint(selectedStaffProfile);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedStaffProfileId) return;
    const nextEmail = staffWorkEmailLabel(inviteReadyProfiles.find(profile => profile.id === selectedStaffProfileId));
    if (!nextEmail) {
      setSelectedStaffProfileId('');
      return;
    }
    setEmail(nextEmail);
  }, [inviteReadyProfiles, selectedStaffProfileId]);

  function onSelectStaffProfile(profileId: string) {
    setSelectedStaffProfileId(profileId);
    setError(null);
    setSuccess(null);
    const profile = inviteReadyProfiles.find(candidate => candidate.id === profileId);
    setEmail(staffWorkEmailLabel(profile) ?? '');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const inviteEmail = email.trim();
    setError(null);
    setSuccess(null);
    if (selectedStaffProfile && selectedStaffEmail && inviteEmail !== selectedStaffEmail) {
      setError('Use the selected Team profile work email for this setup invite.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await authClient.organization.inviteMember({
      email: inviteEmail,
      role,
      organizationId: orgId,
      resend: true,
    } as InviteMemberPayload);
    setSubmitting(false);
    if (err) {
      setError(err.message || 'Could not send invitation.');
      return;
    }
    setEmail('');
    setRole('member');
    setSelectedStaffProfileId('');
    setSuccess(
      invitationEmailEnabled
        ? `Invite sent to ${inviteEmail}.`
        : `Invite created for ${inviteEmail}, but email delivery is off.`,
    );
    await onDone();
  }

  return (
    <SectionCard className="p-0">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex w-full flex-col gap-3 px-5 py-4 text-left md:flex-row md:items-center md:justify-between"
        aria-expanded={open}
      >
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Add teammate</div>
          <h2 className="mt-1 text-lg font-semibold text-slate-950">Add a team member</h2>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Add their work email so they can join Oracord and connect their Workspace account if needed.
          </p>
        </div>
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
          {open ? 'Close' : 'Add team member'}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-5 py-4">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              {success}
            </div>
          )}
          {inviteReadyProfiles.length > 0 && (
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-3">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-blue-700" htmlFor="staff-setup-profile">
                Team member
              </label>
              <select
                id="staff-setup-profile"
                value={selectedStaffProfileId}
                onChange={e => onSelectStaffProfile(e.target.value)}
                disabled={submitting || inviteReadyProfiles.length === 0}
                className="mt-1 w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-sm shadow-sm disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">{inviteReadyProfiles.length ? 'Choose an existing Team profile' : 'No available Team profiles'}</option>
                {inviteReadyProfiles.map(profile => (
                  <option key={profile.id} value={profile.id}>{staffProfileOptionLabel(profile)}</option>
                ))}
              </select>
              <p className="mt-2 text-xs leading-5 text-blue-800">
                {selectedStaffProfile ? inviteHint : 'Choose a saved Team profile or type the work email below.'}
              </p>
            </div>
          )}
          <form onSubmit={onSubmit} className="mt-4 grid gap-2 md:grid-cols-[minmax(220px,1fr)_220px_auto]">
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="work.email@company.com"
              className="rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <select
              value={accessModeForRole(role)}
              onChange={e => setRole(roleForAccessMode(e.target.value as TeamAccessMode))}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm"
              aria-label="User permission"
            >
              <option value="mobile_app_only">Mobile app only</option>
              <option value="app_and_dashboard">App + dashboard</option>
              <option value="owner_dashboard">Owner + dashboard</option>
            </select>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? 'Sending…' : 'Send invite'}
            </button>
          </form>
          <p className="mt-2 text-xs text-slate-500">
            {invitationEmailEnabled
              ? 'Oracord will email the invite automatically.'
              : 'The invite record will be created, but email delivery is currently off.'}
          </p>
        </div>
      )}
    </SectionCard>
  );
}

